import { useEffect, useMemo, useRef, useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'

import { cn } from '@/utils/cn'
import { useRouter } from '@/utils/navigation'
import { AuthContext, getAuthErrorCode, getAuthErrorStatus, register, verifyToken } from '@/stores/auth'
import { useToast } from '@/components/base'
import { RegisterForm, type RegisterValues } from '@/components/form'
import { ArrowBack, Close } from '@/components/icon'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import ScreenHeader from '@/components/ScreenHeader'

export type RegisterStep = 'register' | 'verify' | 'finish'

export default function RegisterScreen() {
  const [step, setStep] = useState<RegisterStep>('register')
  const router = useRouter()
  const { t, i18n } = useTranslation()
  const loading = useLoadingIndicator()
  const toast = useToast()
  const auth = AuthContext.useActorRef()
  const completingRegistration = useRef(false)
  const loggedIn = AuthContext.useSelector((snapshot) => snapshot.matches('LOGGED_IN'))

  useEffect(() => {
    if (loggedIn && !completingRegistration.current) {
      router.replace('/home')
    }
  }, [loggedIn, router])

  useEffect(() => {
    const subscription = auth.subscribe((snapshot) => {
      if (step === 'verify' && snapshot.matches('LOGGED_IN')) {
        loading.stop()
        setStep('finish')
      }
    })

    return subscription.unsubscribe
  }, [auth, step])

  const goToRegisterStep = () => {
    setStep('register')
  }

  const handleRegister = async (data: RegisterValues) => {
    loading.start()

    try {
      await register(data.phoneNumber.code, data.phoneNumber.number, i18n.language, data.newPassword)

      // console.log('handleRegister', data)
      toast.show({ variant: 'success', description: t('auth:toast.success.sendVerificationCode') })
      // send request to get verify code
      setStep('verify')
    } catch (e: any) {
      console.log('handleRegister failed', e)
      throw e
    } finally {
      loading.stop()
    }
  }

  const reSendOTP = async (data: RegisterValues) => {
    // OtpInput owns the success/error toast and countdown. Let it receive the
    // structured API error so rate-limit codes are rendered correctly.
    await register(data.phoneNumber.code, data.phoneNumber.number, i18n.language, data.newPassword)
  }

  const handleSubmit = async (data: RegisterValues) => {
    loading.start()

    try {
      //#TODO this should probably just be hidden inside authMachine
      // as a Signup event type that will verify the token and set to logged in
      const resp = await verifyToken(data.newPassword, data.verifyCode, data.phoneNumber.number, data.phoneNumber.code)
      completingRegistration.current = true
      auth.send({
        type: 'SIGNUP_LOGIN',
        auth: resp
      })
    } catch (e: any) {
      console.log('OTP Failed', e)
      const errorCode = getAuthErrorCode(e)
      const status = getAuthErrorStatus(e)
      if (errorCode === 1007) {
        toast.show({ variant: 'error', description: t('auth:toast.error.noAccess') })
      } else if (errorCode === 1024 || status === 429) {
        toast.show({ variant: 'error', description: t('auth:toast.error.otpVerificationRateLimited') })
      } else {
        toast.show({ variant: 'error', description: t('auth:toast.error.otpFailed') })
      }
      loading.stop() // AuthState listener will handle this for the normal case
    }
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <Header step={step} goToRegisterStep={goToRegisterStep} />
        }}
      />

      <SafeAreaView className={'flex-1 bg-background-primary'}>
        <RegisterForm
          step={step}
          onRegister={handleRegister}
          onSubmit={handleSubmit}
          onFinish={() => router.replace('/home')}
          requestOTP={reSendOTP}
        />
      </SafeAreaView>
    </>
  )
}

function Header({ step, goToRegisterStep }: { step: RegisterStep; goToRegisterStep: () => void }) {
  const { t } = useTranslation()
  const router = useRouter()
  const { top } = useSafeAreaInsets()
  const title = useMemo(() => {
    switch (step) {
      case 'verify':
        return t('auth:safetyVerification')
      default:
        return ''
    }
  }, [t, step])

  return (
    <View className={'bg-background-primary'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={
          step === 'register' ? (
            <Pressable className={'p-2'} onPress={() => router.back()}>
              {({ pressed }) => <ArrowBack className={cn('size-6 text-text-tertiary', pressed && 'opacity-50')} />}
            </Pressable>
          ) : (
            <Pressable
              className={'p-2'}
              onPress={() => (step === 'verify' ? goToRegisterStep() : router.replace('/home'))}
            >
              {({ pressed }) => <Close className={cn('size-6 text-text-tertiary', pressed && 'opacity-50')} />}
            </Pressable>
          )
        }
        centerElement={<Text className={'text-base font-bold text-text-primary'}>{title}</Text>}
      />
    </View>
  )
}
